<script setup lang="ts" generic="T extends object">
/**
 * PTable — table éditoriale data-dense pour les listes (audit log,
 * API keys, users, etc.).
 *
 * Pattern Revelio : thead bg surface-alt, th font-mono uppercase 10px
 * tracking-wider, td font 13px tabular-nums sur chiffres, hover row
 * background surface-alt, border-bottom 1px line entre rows.
 *
 * Générique sur le type de ligne `T`. Les colonnes sont déclarées par
 * le parent ; chaque cellule peut être rendue via slot scopé
 * `cell-<key>`. Pas de tri/filter intégré V1 — c'est de la présentation
 * pure, la logique est côté composable.
 *
 * Le clic sur une row émet `row-click` (utile pour drill-down vers slide-over).
 */
interface Column {
    key: string
    label: string
    width?: string
    align?: 'left' | 'center' | 'right'
    /** Si true, la cellule utilise tabular-nums (chiffres alignés). */
    tabular?: boolean
    /** Si true, la cellule utilise font-mono (IDs, emails, codes). */
    mono?: boolean
}

interface Props {
    columns: Column[]
    rows: T[]
    rowKey: keyof T & string
    loading?: boolean
    emptyMessage?: string
    /** Si true, les rows ont cursor:pointer et émettent row-click. */
    interactive?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    loading: false,
    emptyMessage: '',
    interactive: false,
})

const emit = defineEmits<{
    (e: 'row-click', row: T): void
}>()

void props
</script>

<template>
    <div class="p-table-wrapper" role="region" aria-label="data table">
        <table class="p-table">
            <thead>
                <tr>
                    <th
                        v-for="col in columns"
                        :key="col.key"
                        :style="{ width: col.width, textAlign: col.align ?? 'left' }"
                        class="p-table__th"
                    >
                        {{ col.label }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading" class="p-table__row p-table__row--state">
                    <td :colspan="columns.length" class="p-table__cell p-table__cell--state">
                        <span class="p-animate-pulse-dot" style="margin-right: 4px;"></span>
                        {{ $t('common.loading') }}
                    </td>
                </tr>
                <tr
                    v-else-if="rows.length === 0"
                    class="p-table__row p-table__row--state"
                >
                    <td :colspan="columns.length" class="p-table__cell p-table__cell--state">
                        {{ emptyMessage || $t('common.noResults') }}
                    </td>
                </tr>
                <tr
                    v-for="row in rows"
                    v-else
                    :key="String(row[rowKey])"
                    :class="['p-table__row', { 'p-table__row--interactive': interactive }]"
                    @click="interactive && emit('row-click', row)"
                >
                    <td
                        v-for="col in columns"
                        :key="col.key"
                        :class="[
                            'p-table__cell',
                            { 'p-tabular': col.tabular, 'p-table__cell--mono': col.mono },
                        ]"
                        :style="{ textAlign: col.align ?? 'left' }"
                    >
                        <slot
                            :name="`cell-${col.key}`"
                            :row="row"
                            :value="(row as Record<string, unknown>)[col.key]"
                        >
                            {{ (row as Record<string, unknown>)[col.key] }}
                        </slot>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style scoped>
.p-table-wrapper {
    width: 100%;
    overflow-x: auto;
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    background: var(--p-color-surface);
}

.p-table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--p-font-sans);
    font-size: var(--p-text-sm);
}

.p-table__th {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink-5);
    font-family: var(--p-font-mono);
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    font-size: var(--p-text-2xs);
    padding: 10px 16px;
    border-bottom: 1px solid var(--p-color-line);
    text-align: left;
}

.p-table__row {
    transition: background var(--p-motion-fast) var(--p-ease-out);
}
.p-table__row + .p-table__row .p-table__cell {
    border-top: 1px solid var(--p-color-line);
}
.p-table__row:not(.p-table__row--state):hover {
    background: var(--p-color-surface-alt);
}
.p-table__row--interactive {
    cursor: pointer;
}

.p-table__cell {
    padding: 12px 16px;
    color: var(--p-color-ink);
    vertical-align: middle;
}
.p-table__cell--mono {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-4);
}

.p-table__cell--state {
    text-align: center;
    color: var(--p-color-ink-5);
    padding: var(--p-space-10);
    font-size: var(--p-text-xs);
}
</style>
